import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicesApiAdapter } from '../adapters/InvoicesApiAdapter';
import { Invoice } from '../models/Invoices';

export interface PostContalinkInvoiceData {
  invoiceId: number;
  erp_number: string;
  erp_id: string;
  grand_total: number;
  pdf: File;
}

export const usePostContalinkInvoice = () => {
  const { t } = useTranslation('invoicesModule');
  const { toast } = useToast();
  const [isPosting, setIsPosting] = useState(false);

  const postContalinkInvoice = async (
    data: PostContalinkInvoiceData,
    onSuccess?: (response: Invoice) => void,
  ): Promise<Invoice | null> => {
    setIsPosting(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new InvoicesApiAdapter(httpClient);
      const response = await adapter.postInvoiceToContalink(data.invoiceId, {
        erp_number: data.erp_number,
        erp_id: data.erp_id,
        grand_total: data.grand_total,
        pdf: data.pdf,
      });

      toast({
        title: t('toast.contalink.success.title', 'Factura registrada en Contalink'),
        description: t('toast.contalink.success.description', {
          erpNumber: data.erp_number,
          defaultValue: `Factura ${data.erp_number} registrada en Contalink`,
        }),
        variant: 'default',
      });

      onSuccess?.(response);
      return response;
    } catch (error: any) {
      if (error?.response?.status === 422 && error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        const firstError = Object.values(errors)[0] as string[];

        toast({
          title: t('toast.contalink.error.title', 'Error de validación'),
          description: firstError[0] || t('toast.contalink.error.validation', 'Datos inválidos'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('toast.contalink.error.title', 'Error al registrar en Contalink'),
          description:
            error?.response?.data?.message ||
            error?.message ||
            t('toast.contalink.error.unexpected', 'Error inesperado. Inténtalo de nuevo.'),
          variant: 'destructive',
        });
      }

      return null;
    } finally {
      setIsPosting(false);
    }
  };

  return {
    postContalinkInvoice,
    isPosting,
  };
};
